/**
 * The unit a set of values reads best in.
 *
 * The export converts every value of a curated key into one unit of its
 * dimension (metres, kilograms, hours, hertz…), so a range of 250 km
 * arrives as 250000 m and a frame time as 0.0000139 h. Here a unit of the
 * same ladder is picked for display: the largest one in which the largest
 * value still reads at least one (or the step's own threshold). A unit
 * outside every ladder (km/h, °/h, %, inches, the document's own "nmi")
 * stays as it is.
 */

// One step of a ladder: its unit, how many base units it holds, and the
// smallest value it is picked for (one, unless said otherwise)
interface Step {
  unit: string;
  size: number;
  from?: number;
  // Read as this unit, never picked for display: "cm" reads as mm or m
  sourceOnly?: boolean;
}

const MICRO = "µ";

const HOUR = 3600;
const YEAR = 365.25 * 24 * HOUR;

// Each ladder from its smallest step up
const LADDERS: Step[][] = [
  [
    { unit: `${MICRO}m`, size: 1e-6 },
    { unit: "mm", size: 1e-3 },
    { unit: "cm", size: 1e-2, sourceOnly: true },
    { unit: "m", size: 1 },
    { unit: "km", size: 1e3 },
  ],
  [
    { unit: `${MICRO}g`, size: 1e-9 },
    { unit: "mg", size: 1e-6 },
    { unit: "g", size: 1e-3 },
    { unit: "kg", size: 1 },
    { unit: "t", size: 1e3 },
  ],
  [
    { unit: "ps", size: 1e-12 },
    { unit: "ns", size: 1e-9 },
    { unit: `${MICRO}s`, size: 1e-6 },
    { unit: "ms", size: 1e-3 },
    { unit: "s", size: 1 },
    { unit: "min", size: 60 },
    { unit: "h", size: HOUR },
    // Hours read well up to a few days of endurance; a design life of
    // 87,600 hours reads as ten years
    { unit: "years", size: YEAR, from: 2 },
  ],
  [
    { unit: "Hz", size: 1 },
    { unit: "kHz", size: 1e3 },
    { unit: "MHz", size: 1e6 },
    { unit: "GHz", size: 1e9 },
  ],
  [
    { unit: "mW", size: 1e-3 },
    { unit: "W", size: 1 },
    { unit: "kW", size: 1e3 },
    { unit: "MW", size: 1e6 },
  ],
  [
    { unit: "Wh", size: 1 },
    { unit: "kWh", size: 1e3 },
    { unit: "MWh", size: 1e6 },
  ],
  [
    { unit: "mV", size: 1e-3 },
    { unit: "V", size: 1 },
    { unit: "kV", size: 1e3 },
  ],
  [
    { unit: `${MICRO}A`, size: 1e-6 },
    { unit: "mA", size: 1e-3 },
    { unit: "A", size: 1 },
  ],
  [
    { unit: "N", size: 1 },
    { unit: "kN", size: 1e3 },
    { unit: "MN", size: 1e6 },
  ],
  [
    { unit: "m²", size: 1 },
    { unit: "km²", size: 1e6 },
  ],
  [
    { unit: "MB", size: 1e-3 },
    { unit: "GB", size: 1 },
    { unit: "TB", size: 1e3 },
  ],
  [
    { unit: "kbps", size: 1e-3 },
    { unit: "Mbps", size: 1 },
    { unit: "Gbps", size: 1e3 },
  ],
  [
    { unit: "USD", size: 1 },
    { unit: "million USD", size: 1e6 },
    { unit: "billion USD", size: 1e9 },
  ],
];

// Each unit's ladder and step, by its spelling; the Greek mu reads as the
// micro sign
const STEPS = new Map<string, { ladder: Step[]; step: Step }>(
  LADDERS.flatMap((ladder) =>
    ladder.map((step) => [step.unit, { ladder, step }] as const),
  ),
);

const spelling = (unit: string): string => unit.trim().replace(/^μ/, MICRO);

/** The unit values read in, and what to divide them by to read in it. */
export interface DisplayUnit {
  unit: string | null;
  divisor: number;
}

/**
 * The unit *values*, stated in *unit*, read best in; the unit itself where
 * it is on no ladder or there is no value to go by.
 */
export const displayUnit = (unit: string | null, values: number[]): DisplayUnit => {
  const found = unit ? STEPS.get(spelling(unit)) : undefined;
  const largest = Math.max(0, ...values.map(Math.abs).filter(Number.isFinite));
  if (!found || largest === 0) {
    return { unit, divisor: 1 };
  }
  const { ladder, step } = found;
  const inBase = largest * step.size;
  const targets = ladder.filter((candidate) => !candidate.sourceOnly);
  // The largest step the largest value reads at least its threshold in;
  // below the smallest, the smallest
  const picked =
    [...targets]
      .reverse()
      .find((candidate) => inBase / candidate.size >= (candidate.from ?? 1)) ?? targets[0];
  return { unit: picked.unit, divisor: picked.size / step.size };
};

/** The numbers of statements, for picking their unit. */
export const statementNumbers = (
  statements: { value_min: number | null; value_max: number | null }[],
): number[] =>
  statements.flatMap((statement) =>
    [statement.value_min, statement.value_max].filter(
      (value): value is number => value !== null,
    ),
  );
